import { create } from "zustand";
import { persist } from "zustand/middleware";

interface CartItem {
  id: string;
  productId: string;
  batchId: string;
  name: string;
  quantity: number;
  mrp: number;
  discount: number;
  gstRate: number;
  total: number;
}

interface CartState {
  items: CartItem[];
  customerId?: string;
  customerName?: string;
  cartSavedAt?: number; // Timestamp for staleness detection
  isStale: boolean;

  // Actions
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  setCustomer: (id: string, name: string) => void;

  // Computed
  totalAmount: () => number;
  totalItems: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      customerId: undefined,
      customerName: undefined,
      cartSavedAt: undefined,
      isStale: false,

      addItem: (item) =>
        set((state) => ({
          items: [...state.items, item],
          cartSavedAt: Date.now(),
        })),

      removeItem: (id) =>
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        })),

      updateQuantity: (id, quantity) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  quantity,
                  total: item.mrp * quantity * (1 - item.discount / 100),
                }
              : item,
          ),
        })),

      clearCart: () => set({ items: [], customerId: undefined, customerName: undefined, isStale: false }),

      setCustomer: (id, name) =>
        set({
          customerId: id,
          customerName: name,
          cartSavedAt: Date.now(),
        }),

      totalAmount: () => {
        const state = get();
        return state.items.reduce((sum, item) => sum + item.total, 0);
      },

      totalItems: () => {
        const state = get();
        return state.items.reduce((sum, item) => sum + item.quantity, 0);
      },
    }),
    {
      name: "pos-cart-storage",
      partialize: (state) => ({
        items: state.items,
        customerId: state.customerId,
        customerName: state.customerName,
        cartSavedAt: state.cartSavedAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.cartSavedAt && state.items?.length > 0) {
          const now = Date.now();
          const fourHoursMs = 4 * 60 * 60 * 1000;
          state.isStale = now - state.cartSavedAt > fourHoursMs;
        } else {
          state!.isStale = false;
        }
      },
    },
  ),
);
